import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { useCan } from '@/hooks/usePermissions';
import {
  useClinicRoles,
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
} from '@/hooks/useRoles';
import {
  PERMISSION_GROUPS,
  ROLES_MANAGE,
  permissionDescription,
  permissionGroupLabel,
  permissionLabel,
  type PermissionKey,
} from '@/lib/permissions';
import type { ClinicRole } from '@/types/api';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';

/**
 * Roles and what each one may do.
 *
 * The permission catalogue rendered here is the same constant the API guards
 * with — it ships in the bundle via `@synapse/permissions` rather than over the
 * wire, so this list cannot drift from what the server enforces.
 */
export function RolesPage() {
  const { t } = useTranslation();
  const can = useCan();
  const { data: roles = [], isLoading, error } = useClinicRoles();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ClinicRole | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClinicRole | null>(null);

  const canManage = can(ROLES_MANAGE);
  // A clinic must keep at least one role that grants access management, so the
  // last one cannot be deleted. Showing that as a disabled button with a reason
  // beats letting the request go out and returning a 409.
  const managerRoleCount = useMemo(
    () => roles.filter((r) => r.permissions.includes(ROLES_MANAGE)).length,
    [roles],
  );

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">
            {t('roles.title')}
          </h1>
          <p className="mt-1 text-[13.5px] text-muted">{t('roles.subtitle')}</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={16} /> {t('roles.newRole')}
          </Button>
        )}
      </header>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">{t('roles.loading')}</div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-[13px] text-destructive">
            {translateApiError(error, { fallback: t('roles.loadFailed') })}
          </div>
        ) : roles.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">{t('roles.empty')}</div>
        ) : (
          <ul className="divide-y divide-line">
            {roles.map((role) => {
              const grantsManage = role.permissions.includes(ROLES_MANAGE);
              const isLastManagerRole = grantsManage && managerRoleCount <= 1;
              return (
                <li key={role.id} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[14px] font-semibold tracking-[-0.01em]">
                        {role.name}
                      </span>
                      {grantsManage && (
                        <Badge tone="info" size="sm">
                          {t('roles.managesAccess')}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-[12.5px] text-muted">
                      {role.description || t('roles.noDescription')}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="hidden text-[11.5px] tabular-nums text-muted sm:inline">
                      {t('roles.permissionCount', { count: role.permissions.length })}
                    </span>
                    <span className="text-[11.5px] tabular-nums text-muted">
                      {t('roles.memberCount', { count: role.memberCount })}
                    </span>
                    {canManage && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label={`${t('common.edit')} ${role.name}`}
                          onClick={() => setEditTarget(role)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-muted hover:bg-paper-2 hover:text-ink"
                        >
                          <Icon name="edit" size={14} />
                        </button>
                        <button
                          type="button"
                          aria-label={`${t('common.delete')} ${role.name}`}
                          disabled={isLastManagerRole}
                          title={isLastManagerRole ? t('roles.lastManagerRoleHint') : undefined}
                          onClick={() => setDeleteTarget(role)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-muted hover:bg-paper-2 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted"
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <CardContent className="border-t border-line bg-paper-2 px-4 py-2.5 text-[11.5px] text-muted sm:px-5">
          {t('roles.footer')}
        </CardContent>
      </Card>

      {createOpen && <RoleDialog onClose={() => setCreateOpen(false)} />}
      {editTarget && <RoleDialog role={editTarget} onClose={() => setEditTarget(null)} />}
      {deleteTarget && (
        <DeleteRoleDialog role={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}

/**
 * Create and edit share one dialog: the fields and validation are identical,
 * and the only difference is which mutation runs on submit.
 */
function RoleDialog({ role, onClose }: { role?: ClinicRole; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissions, setPermissions] = useState<PermissionKey[]>(role?.permissions ?? []);
  const create = useCreateRole();
  const update = useUpdateRole();
  const mutation = role ? update : create;

  const can = useCan();
  const selected = useMemo(() => new Set(permissions), [permissions]);
  // What the role carried when the dialog opened. Frozen deliberately: these
  // keys are not being granted by this edit, so the escalation rule does not
  // apply to them and they must stay removable.
  const initiallySelected = useMemo(() => new Set(role?.permissions ?? []), [role]);
  // At least one permission: a role granting nothing is indistinguishable from
  // having no role, and the API rejects it too.
  const valid = name.trim().length > 0 && permissions.length > 0;

  function toggle(key: PermissionKey) {
    setPermissions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  const submit = async () => {
    if (!valid) return;
    try {
      if (role) {
        await update.mutateAsync({
          id: role.id,
          patch: {
            name: name.trim(),
            description: description.trim() || null,
            permissions,
          },
        });
      } else {
        await create.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          permissions,
        });
      }
      onClose();
    } catch {
      // Inline below — the API refuses self-escalation and lockouts, and the
      // reason has to reach the person who tried.
    }
  };

  return (
    <Modal
      title={role ? t('roles.editRole', { name: role.name }) : t('roles.newRole')}
      description={t('roles.dialogDesc')}
      onClose={onClose}
      isPending={mutation.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button full onClick={submit} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('roles.name')}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('roles.namePlaceholder')}
          autoFocus
        />
      </Field>
      <Field label={t('roles.description')}>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('roles.descriptionPlaceholder')}
        />
      </Field>

      <div className="flex flex-col gap-3">
        <span className="text-[12.5px] font-medium text-ink-2">{t('roles.permissions')}</span>
        {PERMISSION_GROUPS.map((group) => (
          <fieldset key={group.id} className="rounded-md border border-line p-3">
            <legend className="px-1 text-[11px] uppercase tracking-[0.06em] text-muted">
              {permissionGroupLabel(t, group.id)}
            </legend>
            <div className="flex flex-col gap-1.5">
              {group.keys.map((key) => {
                // You cannot grant what you do not hold — the server refuses it
                // by name. Offering the checkbox anyway means finding that out
                // only after filling in the form, so it is disabled here with
                // the reason attached. Already-granted keys stay editable even
                // if the caller lacks them: REMOVING one is always allowed, and
                // trimming an over-broad role is the main thing a narrow
                // manager is for.
                const cannotGrant = !can(key) && !initiallySelected.has(key);
                return (
                  <label
                    key={key}
                    title={cannotGrant ? t('roles.cannotGrantHint') : undefined}
                    className={cn(
                      'flex items-start gap-2 rounded-md px-1.5 py-1',
                      cannotGrant
                        ? 'cursor-not-allowed opacity-55'
                        : 'cursor-pointer hover:bg-paper-2',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-[3px]"
                      checked={selected.has(key)}
                      disabled={cannotGrant}
                      onChange={() => toggle(key)}
                    />
                    <span className="min-w-0">
                      <span className="block text-[12.5px] font-medium tracking-[-0.01em]">
                        {permissionLabel(t, key)}
                      </span>
                      <span className="block text-[11.5px] text-muted">
                        {cannotGrant
                          ? t('roles.cannotGrantHint')
                          : permissionDescription(t, key)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      {mutation.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {translateApiError(mutation.error, { fallback: t('roles.saveFailed') })}
        </div>
      )}
    </Modal>
  );
}

function DeleteRoleDialog({ role, onClose }: { role: ClinicRole; onClose: () => void }) {
  const { t } = useTranslation();
  const remove = useDeleteRole();

  const submit = async () => {
    try {
      await remove.mutateAsync(role.id);
      onClose();
    } catch {
      // Inline below.
    }
  };

  return (
    <Modal
      title={t('roles.deleteRole', { name: role.name })}
      // Members are allowed to end up with no roles at all — the alternative
      // would be making a role impossible to retire without reassigning
      // everyone first — so the count is a warning, not a blocker.
      description={
        role.memberCount > 0
          ? t('roles.deleteWarning', { count: role.memberCount })
          : t('roles.deleteConfirm')
      }
      onClose={onClose}
      isPending={remove.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={remove.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" full onClick={submit} disabled={remove.isPending}>
            {remove.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </>
      }
    >
      {remove.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {translateApiError(remove.error, { fallback: t('roles.deleteFailed') })}
        </div>
      )}
    </Modal>
  );
}
